import { createFileRoute, Link, notFound } from "@tanstack/react-router"
import { Trophy } from "lucide-react"
import {
  getBlogPost,
  formatBlogDate,
  blogPosts,
  type BlogPost,
} from "@/lib/blog-posts"
import { defaultMeta, absoluteUrl, SITE_NAME } from "@/lib/seo"

// Two winner accents: coral (the home product) and a cool sky tone (the rival),
// so the two sides read differently while sharing the same badge shape.
type WinTone = `primary` | `alt`
const winStyles: Record<WinTone, { bg: string; border: string; icon: string }> = {
  primary: { bg: `bg-primary/10`, border: `border-primary/30`, icon: `text-primary` },
  alt: { bg: `bg-sky-500/10`, border: `border-sky-500/30`, icon: `text-sky-500` },
}

// Per-category "Winner: …" callout. A trophy in the side's accent for a
// decisive round; a neutral pill when it's a tie.
function VerdictBadge({
  verdict,
}: {
  verdict: { winner: string; tie?: boolean; tone?: WinTone }
}) {
  if (verdict.tie) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-sm font-semibold text-muted-foreground">
        Winner: {verdict.winner}
      </div>
    )
  }
  const s = winStyles[verdict.tone ?? `primary`]
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold text-foreground ${s.border} ${s.bg}`}
    >
      <Trophy className={`h-3.5 w-3.5 ${s.icon}`} />
      Winner: {verdict.winner}
    </div>
  )
}

export const Route = createFileRoute(`/_public/blogs/$slug`)({
  ssr: true,
  head: ({ params }) => {
    const post = getBlogPost(params.slug)
    if (!post) return {}
    return defaultMeta({
      title: post.title,
      description: post.excerpt,
      path: `/blogs/${post.slug}`,
      image: post.coverImage,
      ogType: `article`,
      extra: [
        { property: `article:author`, content: post.author },
        { property: `article:published_time`, content: new Date(post.date).toISOString() },
      ],
    })
  },
  loader: ({ params }) => {
    const post = getBlogPost(params.slug)
    if (!post) throw notFound()
    return post
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center space-y-4">
      <h1 className="text-2xl font-bold">Post not found</h1>
      <p className="text-muted-foreground">
        This article doesn't exist or may have moved.
      </p>
      <Link to="/blogs" className="text-primary font-medium hover:underline">
        ← Back to Blog
      </Link>
    </div>
  ),
})

function BlogPostPage() {
  const post = Route.useLoaderData() as BlogPost
  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2)

  const jsonLd = {
    "@context": `https://schema.org`,
    "@type": `BlogPosting`,
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: {
      "@type": `Organization`,
      name: post.author,
      url: absoluteUrl(`/`),
    },
    publisher: {
      "@type": `Organization`,
      name: SITE_NAME,
      url: absoluteUrl(`/`),
    },
    image: post.coverImage,
    url: absoluteUrl(`/blogs/${post.slug}`),
    mainEntityOfPage: absoluteUrl(`/blogs/${post.slug}`),
  }

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 space-y-10">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Link
        to="/blogs"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        ← Blog
      </Link>

      <header className="space-y-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{post.author}</span>
          <span>·</span>
          <span>{formatBlogDate(post.date)}</span>
          <span>·</span>
          <span>{post.readTime}</span>
        </div>
        <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
          {post.title}
        </h1>
        <p className="text-xl text-muted-foreground leading-relaxed">
          {post.excerpt}
        </p>
        <div className="rounded-2xl overflow-hidden h-72 sm:h-96 bg-muted/40">
          <img
            src={post.coverImage}
            alt={post.coverAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <hr className="border-border" />
      </header>

      <div className="space-y-6 text-base leading-relaxed">
        {post.sections.map((section, i) => (
          <div key={i} className="space-y-3">
            {section.heading && (
              <h2 className="text-xl font-bold">{section.heading}</h2>
            )}
            {section.body &&
              (Array.isArray(section.body)
                ? section.body
                : [section.body]
              ).map((para, j) => (
                <p key={j} className="text-muted-foreground">
                  {para}
                </p>
              ))}
            {section.code && (
              <pre className="rounded-xl border border-border bg-muted/50 px-4 py-3 text-sm font-mono text-foreground overflow-x-auto">
                <code>{section.code}</code>
              </pre>
            )}
            {section.image && (
              <figure className="space-y-2">
                <div className="rounded-2xl overflow-hidden border border-border bg-muted/40">
                  <img
                    src={section.image.src}
                    alt={section.image.alt}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
                {section.image.caption && (
                  <figcaption className="text-xs text-muted-foreground text-center">
                    {section.image.caption}
                  </figcaption>
                )}
              </figure>
            )}
            {section.cta && (
              <Link
                to={section.cta.to}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                {section.cta.label} →
              </Link>
            )}
            {section.verdict && <VerdictBadge verdict={section.verdict} />}
          </div>
        ))}
      </div>

      {related.length > 0 && (
        <section className="pt-8 border-t border-border space-y-4">
          <h2 className="text-lg font-bold">More posts</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {related.map((p) => (
              <Link
                key={p.slug}
                to="/blogs/$slug"
                params={{ slug: p.slug }}
                className="group block rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-muted/40 transition-all duration-200 overflow-hidden"
              >
                <div className="h-28 overflow-hidden bg-muted/40">
                  <img
                    src={p.coverImage}
                    alt={p.coverAlt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 space-y-1">
                  <p className="text-xs text-muted-foreground">{formatBlogDate(p.date)}</p>
                  <p className="text-sm font-semibold leading-snug group-hover:text-primary transition-colors">
                    {p.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-border pt-8">
        <Link
          to="/blogs"
          className="text-sm font-medium text-primary hover:underline"
        >
          ← Back to all posts
        </Link>
      </footer>
    </article>
  )
}
